import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  ListItemAvatar,
  Avatar,
  Chip,
  CircularProgress,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  Divider,
  IconButton,
} from '@mui/material';
import {
  Search as SearchIcon,
  Inventory as ContainerIcon,
  Close as CloseIcon,
  FilterList as FilterIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { useContainerCore } from '../../../../core/modules/container';
import { useCompanyCore } from '../../../../core/modules/company';
import { ContainerForClassification } from '../../../../core/interfaces/container.interface';

interface ContainerSearchDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect: (container: ContainerForClassification) => void;
  title: string;
  companyId?: number;
}

export const ContainerSearchDialog: React.FC<ContainerSearchDialogProps> = ({
  open,
  onClose,
  onSelect,
  title,
  companyId,
}) => {
  const [copySuccess, setCopySuccess] = useState<string | null>(null);

  const handleCopyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess(`Código "${text}" copiado al portapapeles`);
      setTimeout(() => setCopySuccess(null), 3000);
    } catch (err) {
      // Fallback
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopySuccess(`Código "${text}" copiado al portapapeles`);
      setTimeout(() => setCopySuccess(null), 3000);
    }
  };
  
  const { containers, getContainers, clearContainer } = useContainerCore();
  const { companyId: contextCompanyId } = useCompanyCore();

  // Cast containers to the extended type
  const typedContainers = containers as ContainerForClassification[] | null;

  // Estado local
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedContainerType, setSelectedContainerType] = useState<string>('all');
  const [loading, setLoading] = useState(false);

  // Usar companyId del contexto si no se proporciona
  const effectiveCompanyId = companyId || contextCompanyId;

  // Monitorear cambios en el estado de contenedores
  useEffect(() => {
    // Log removed for production
  }, [containers, typedContainers]);

  // Cargar contenedores cuando se abre el diálogo
  useEffect(() => {
    if (open && effectiveCompanyId) {
      setLoading(true);
      getContainers(effectiveCompanyId, {})
        .then(() => {
          setLoading(false);
        })
        .catch((error) => {
          console.error('Error loading containers:', error);
          setLoading(false);
        });
    }
  }, [open, effectiveCompanyId, getContainers]);

  // Limpiar estado cuando se cierra
  useEffect(() => {
    if (!open) {
      setSearchTerm('');
      setSelectedStatus('all');
      setSelectedCategory('all');
      setSelectedContainerType('all');
      clearContainer();
    }
  }, [open, clearContainer]);

  // Filtrar contenedores según los criterios
  const filteredContainers = useMemo(() => {
    if (!typedContainers) return [];

    return typedContainers.filter((container) => {
      // Filtrar por término de búsqueda
      const matchesSearch = searchTerm === '' || 
        container.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        container.description?.toLowerCase().includes(searchTerm.toLowerCase());

      // Filtrar por tipo
      const matchesCategory = selectedCategory === 'all' || 
        (container.container_category?.id?.toString() === selectedCategory.toString()) ||
        (container.container_category_id?.toString() === selectedCategory.toString());

      // Filtrar por estado
      const matchesStatus = selectedStatus === 'all' || 
        container.container_status?.id?.toString() === selectedStatus.toString() ||
        (selectedStatus === 'open' && (container.container_status?.name === 'Open' || container.container_status_name === 'Open'));

      const matchesContainerType = selectedContainerType === 'all' ||
        container.container_type_id?.toString() === selectedContainerType.toString() ||
        (container.container_category?.container_type_id && container.container_category.container_type_id.toString() === selectedContainerType.toString()) ||
        (container.container_type_name && container.container_type_name === selectedContainerType);

      return matchesSearch && matchesCategory && matchesStatus && matchesContainerType;
    });
  }, [typedContainers, searchTerm, selectedCategory, selectedContainerType, selectedStatus]);

  // Obtener tipos de contenedor únicos
  const containerTypes = useMemo(() => {
    if (!typedContainers) return [];
    
    const types = new Map();
    typedContainers.forEach(container => {
      // Usar container_category preloaded si está disponible, sino usar container_category_id
      if (container.container_category?.id && container.container_category?.name) {
        types.set(container.container_category.id, {
          id: container.container_category.id,
          name: container.container_category.name,
          acronym: container.container_category.acronym,
        });
      } else if (container.container_category_id) {
        types.set(container.container_category_id, {
          id: container.container_category_id,
          name: `Categoría ${container.container_category_id}`,
        });
      }
    });
    
    return Array.from(types.values());
  }, [typedContainers]);

  const containerTypeOptions = useMemo(() => {
    if (!typedContainers) return [];

    const types = new Map<number, { id: number; name: string }>();
    typedContainers.forEach((container) => {
      const typeId = container.container_category?.container_type_id;
      const typeInfo = container.container_category?.container_type;

      if (typeId) {
        const displayName = typeInfo?.name || `Tipo ${typeId}`;
        const description = typeInfo?.description?.trim();
        const label = description ? `(${description}) ${displayName}` : displayName;

        types.set(typeId, { id: typeId, name: label });
      }
    });

    return Array.from(types.values());
  }, [typedContainers]);

  // Obtener estados de contenedor únicos
  const containerStatuses = useMemo(() => {
    if (!typedContainers) return [];
    const statuses = new Map();
    typedContainers.forEach(container => {
      if (container.container_status?.name) {
        statuses.set(container.container_status.id, container.container_status.name);
      } else if (container.container_status_id) {
        statuses.set(container.container_status_id, `Estado ${container.container_status_id}`);
      }
    });
    
    const result = Array.from(statuses.entries()).map(([id, name]) => ({ id, name }));
    return result;
  }, [typedContainers]);

  // Verificar si un contenedor está cerrado
  const isContainerClosed = (container: ContainerForClassification) => {
    // Usar el campo preloaded si está disponible, sino usar los campos legacy

    const statusName = container.container_status?.name || container.container_status_name;
    return container.is_closed || container.status === 'closed' || statusName === 'Closed';
  };

  const handleContainerSelect = (container: ContainerForClassification) => {
    onSelect(container);
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleCategoryChange = (event: any) => {
    setSelectedCategory(event.target.value);
  };

  const handleStatusChange = (event: any) => {
    const value = event.target.value;
    setSelectedStatus(value);
  };

  const handleContainerTypeChange = (event: any) => {
    setSelectedContainerType(event.target.value);
  };

  const getContainerStatusColor = (container: ContainerForClassification) => {
    const statusName = container.container_status?.name || container.container_status_name;
    switch (statusName) {
      case 'Closed':
        return 'error';
      case 'Open':
        return 'success';
      case 'Paused':
        return 'warning';
      case 'Available':
        return 'success';
      default:
        return 'default';
    }
  };

  const getContainerStatusText = (container: ContainerForClassification) => {
   
    const statusName = container.container_status?.name || container.container_status_name;
    switch (statusName) {
      case 'Closed':
        return 'Cerrado';
      case 'Open':
        return 'Abierto';
      case 'Paused':
        return 'Pausado';
      case 'Available':
        return 'Disponible';
      default:
        return statusName || "No disponible";
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { 
          minHeight: 600,
          zIndex: 1300,
        },
      }}
      sx={{
        zIndex: 1300,
        '& .MuiBackdrop-root': {
          zIndex: 1299,
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <ContainerIcon color="primary" />
            <Typography variant="h6">{title}</Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

             <DialogContent>
         <Box display="flex" flexDirection="column" gap={3}>
           {/* Alerta de copia exitosa */}
           {copySuccess && (
             <Alert severity="success" onClose={() => setCopySuccess(null)}>
               {copySuccess}
             </Alert>
           )}

           {/* Filtros */}
           <Box>
             <Typography variant="subtitle1" gutterBottom>
               <FilterIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
               Filtros de búsqueda
             </Typography>
            
            <Grid container spacing={2}>
              {/* Búsqueda por texto */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  placeholder="Buscar por código o descripción"
                  value={searchTerm}
                  onChange={handleSearch}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* Filtro por tipo */}
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Tipo</InputLabel>
                  <Select
                    value={selectedCategory}
                    onChange={handleCategoryChange}
                    label="Tipo"
                  >
                    <MenuItem value="all">Todas las Categorías</MenuItem>
                    {containerTypes.map((type) => (
                      <MenuItem key={type.id} value={type.id}>
                        {type.acronym ? `(${type.acronym}) ${type.name}` : type.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Filtro por tipo de contenedor */}
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Tipo de contenedor</InputLabel>
                  <Select
                    value={selectedContainerType}
                    onChange={handleContainerTypeChange}
                    label="Tipo de contenedor"
                  >
                    <MenuItem value="all">Todos los tipos</MenuItem>
                    {containerTypeOptions.map((type) => (
                      <MenuItem key={type.id} value={type.id}>
                        {type.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Filtro por estado */}
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Estado</InputLabel>
                  <Select
                    value={selectedStatus}
                    onChange={handleStatusChange}
                    label="Estado"
                  >
                    <MenuItem value="all">Todos los estados</MenuItem>
                    <MenuItem value="open">Solo abiertos</MenuItem>
                    {containerStatuses.map((status) => {
                      return (
                        <MenuItem key={status.id} value={status.id}>
                          {status.name}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
                     </Box>

           <Divider />

           {/* Lista de contenedores */}
          <Box>
            <Typography variant="subtitle1" gutterBottom>
              Contenedores disponibles ({filteredContainers.length})
            </Typography>

            {loading ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            ) : filteredContainers.length === 0 ? (
              <Alert severity="info">
                No se encontraron contenedores con los filtros aplicados.
              </Alert>
            ) : (
              <List sx={{ maxHeight: 400, overflow: 'auto' }}>
                {filteredContainers.map((container, index) => (
                  <React.Fragment key={container.id}>
                    <ListItem disablePadding>
                      <ListItemButton
                        onClick={() => handleContainerSelect(container)}
                        disabled={isContainerClosed(container)}
                        sx={{
                          opacity: isContainerClosed(container) ? 0.6 : 1,
                          '&:hover': {
                            backgroundColor: isContainerClosed(container) ? 'transparent' : 'action.hover',
                          },
                        }}
                      >
                        <ListItemAvatar>
                          <Avatar>
                            <ContainerIcon />
                          </Avatar>
                        </ListItemAvatar>
                        
                        <ListItemText
                          primary={
                            <Box display="flex" alignItems="center" gap={1}>
                              <Typography variant="body1" fontWeight="medium">
                                {container.code}
                              </Typography>
                              {container.container_category?.acronym && (
                                <Chip
                                  label={container.container_category?.acronym}
                                  size="small"
                                  variant="outlined"
                                />
                              )}
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleCopyToClipboard(container.code);
                                }}
                                sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main', backgroundColor: 'action.hover' } }}
                                title="Copiar código al portapapeles"
                              >
                                <CopyIcon fontSize="small" />
                              </IconButton>
                              <Chip
                                label={getContainerStatusText(container)}
                                color={getContainerStatusColor(container)}
                                size="small"
                              />
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Typography variant="body2" color="text.secondary">
                                {container.description}
                              </Typography>
                              <Typography variant="caption" display="block">
                                Tipo: {container.container_category?.name || (container.container_category_id ? `Categoría ${container.container_category_id}` : 'N/A')}
                              </Typography>
                              {container.location?.name && (
                                <Typography variant="caption" display="block">
                                  Ubicación: {container.location.name}
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                    
                    {index < filteredContainers.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            )}
          </Box>

                     {/* Información sobre contenedores cerrados */}
           {filteredContainers.some(isContainerClosed) && (
             <Alert severity="warning">
               <Typography variant="body2">
                 Los contenedores marcados como "Cerrado" están deshabilitados y no pueden ser seleccionados.
               </Typography>
             </Alert>
           )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          Cancelar
        </Button>
      </DialogActions>
    </Dialog>
  );
};
